import { useCallback, useEffect, useMemo, useRef, useState, startTransition, FC } from 'react';
import { useListSelection } from '../../hooks';
import { v4 as uuidv4 } from 'uuid';
import styled from 'styled-components';
import { Active, DragEndEvent } from '@dnd-kit/core';

import Switch from '@mui/material/Switch';

import {
  CrossContainerDnDProvider,
  FolderManagerWithActions,
  NgBuildIcon,
  NgCard,
  NgCardContent,
  NgCardTitle,
  NgGridLayout,
  StickyHeader,
} from '../';
import {
  FolderManagerBreadcrumb,
  FolderManagerItemActionNames,
  FolderManagerItemActionTypes,
  FolderManagerMimeTypes,
} from '../../interfaces';

import { PresentationOutline, OutlineSlideSelectData } from '../../pages/Conversations/components/PresentationOutline';
import { updateAtPath, removeAtPath, getAtPath } from '../../pages/Conversations/components/outlineTreeUtils';
import {
  useGetTaxonomyQuery,
  useReplaceTaxonomyMutation,
  useCreateTaxonomyNodeMutation,
  useUpdateTaxonomyNodeMutation,
  useDeleteTaxonomyNodeMutation,
  mapSectionsToTaxonomyInput,
  TaxonomyTreeSlide,
} from '../../services';
import { config } from '../../config';

const StyledColumnGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
`;

const StyledMainSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
`;

const TARGET_ACCEPTED_TYPES = [FolderManagerMimeTypes.PPTX, FolderManagerMimeTypes.SLIDE];
const STICKY_TOP = 65;
const SAVE_DEBOUNCE_MS = 800;

const StyledScrollOutline = styled.div`
  max-height: calc(100vh - ${STICKY_TOP + 140}px);
  overflow-y: auto;
`;

const StyledTextButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 11px;
  color: inherit;
  &:hover {
    text-decoration: underline;
  }
`;

const itemToTaxonomySlide = (item: FolderManagerItem): TaxonomyTreeSlideUI => {
  const slideData = item?.itemData?.slideData || item?.itemData;

  return {
    ID: Number(slideData?.ID) || 0,
    SlideID: Number(slideData?.SlideID ?? slideData?.ID) || 0,
    Title: item.displayName || slideData?.Title || '',
    ThumbnailLocation: item.thumbnailUrl || slideData?.ThumbnailLocation || '',
    LayoutName: slideData?.LayoutName ?? '',
    Order: 0,
    instanceId: uuidv4(),
  };
};

const getOutlineSlideKey = (slide: OutlineSlide) => slide.instanceId;

interface CategoryEditorProps {
  viewStateKey: string;
  slideRepositoryId?: number;
}

interface TaxonomyTreeSlideUI extends TaxonomyTreeSlide {
  instanceId?: string;
    purpose?: string;
    query?: string;
}

export const CategoryEditor: FC<CategoryEditorProps> = ({ viewStateKey, slideRepositoryId }) => {
  const queryArg = slideRepositoryId ? { slideRepositoryId } : undefined;
  const { data: apiSections, isLoading: isLoadingTaxonomy } = useGetTaxonomyQuery(queryArg);
  const [replaceTaxonomy, { isLoading: isSaving }] = useReplaceTaxonomyMutation();
  const [createTaxonomyNode] = useCreateTaxonomyNodeMutation();
  const [updateTaxonomyNode] = useUpdateTaxonomyNodeMutation();
  const [deleteTaxonomyNode] = useDeleteTaxonomyNodeMutation();

  const [sections, setSections] = useState<PresentationOutlineSection<TaxonomyTreeSlideUI>[]>([]);
  const initializedRef = useRef(false);
  const skipNextSaveRef = useRef(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastGoodSectionsRef = useRef<PresentationOutlineSection<TaxonomyTreeSlideUI>[]>([]);
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  useEffect(() => {
    if (!apiSections) return;
    lastGoodSectionsRef.current = apiSections;
    skipNextSaveRef.current = true;
    setSections(apiSections);
    initializedRef.current = true;
  }, [apiSections]);

  useEffect(() => {
    if (!initializedRef.current) return;
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }

    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      const body = mapSectionsToTaxonomyInput(sectionsRef.current);
      const result = await replaceTaxonomy({ body, slideRepositoryId });
      if ('error' in result) {
        skipNextSaveRef.current = true;
        setSections(lastGoodSectionsRef.current);
      } else {
        lastGoodSectionsRef.current = sectionsRef.current;
      }
    }, SAVE_DEBOUNCE_MS);

    return () => clearTimeout(saveTimeoutRef.current);
  }, [sections, replaceTaxonomy]);

  const [selectedSourceItems, setSelectedSourceItems] = useState<any[]>([]);
  const {
    selectedItems: selectedOutlineSlides,
    handleSelect: selectOutlineSlide,
    clearSelection: clearOutlineSelection,
  } = useListSelection<OutlineSlide>(getOutlineSlideKey);

  const [isExternalDragActive, setIsExternalDragActive] = useState(false);
  const [showSlidesMode, setShowSlidesMode] = useState(false);
  const [activeSectionPath, setActiveSectionPath] = useState<number[] | null>(null);
  const outlineActionsRef = useRef<{ expandAll: () => void; collapseAll: () => void } | null>(null);
  const clearSourceSelectionRef = useRef<(() => void) | null>(null);

  const handleSectionSelect = useCallback((path: number[]) => {
    startTransition(() => {
      setActiveSectionPath(path);
    });
  }, []);

  const activeSectionSlides = useMemo(() => {
    if (!activeSectionPath) return [];
    const section = getAtPath(sections, activeSectionPath);
    if (!section) return [];
    return (section.items ?? [])
      .filter((item) => item.kind === 'slide')
      .map((item) => {
        const slide = item.data
        const title = slide.Title || 'Slide';
        const thumbnailUrl = slide.ThumbnailLocation || null;
        return {
          id: slide.SlideID,
          displayName: title,
          name: title,
          type: FolderManagerMimeTypes.SLIDE,
          thumbnailUrl,
          actions: [{ name: FolderManagerItemActionNames.ADD_TO_CATEGORY, type: FolderManagerItemActionTypes.MULTI }],
          itemData: {
            Title: title,
            ID: slide.ID,
            SlideID: slide.SlideID,
            ThumbnailLocation: thumbnailUrl,
            LayoutName: slide.LayoutName ?? null,
          },
        };
      });
  }, [activeSectionPath, sections]);

  const activeSectionBreadcrumbs = useMemo<FolderManagerBreadcrumb[]>(() => {
    if (!activeSectionPath) return [];
    return activeSectionPath.map((_, depth) => {
      const partialPath = activeSectionPath.slice(0, depth + 1);
      const section = getAtPath(sections, partialPath);
      return {
        id: partialPath.join('-'),
        name: section?.displayName || section?.title || 'Category',
      };
    });
  }, [activeSectionPath, sections]);

  const currentDraggedItemsRef = useRef<FolderManagerItem[]>([]);

  const handleDragStartData = useCallback(
    (active: Active) => {
      const data = active.data.current as any;

      if (data?.item) {
        const draggedItem = data.item;
        const thumbnailUrl = data.thumbnailUrl;
        const isInSelection = selectedSourceItems.some((item: any) => item.id === draggedItem.id);

        const items =
          isInSelection && selectedSourceItems.length > 1
            ? selectedSourceItems.map((item: any) => ({
                ...item,
                thumbnailUrl: item.id === draggedItem.id ? thumbnailUrl || item.thumbnailUrl : item.thumbnailUrl,
              }))
            : [{ ...draggedItem, thumbnailUrl: thumbnailUrl || draggedItem.thumbnailUrl }];

        currentDraggedItemsRef.current = items;

        return items;
      }

      if (data?.type === 'section') {
        const section = getAtPath(sections, data.path);
        return section ? [{ displayName: section.displayName || section.title }] : [];
      }

      if (data?.type === 'slide' && data.parentPath) {
        const section = getAtPath(sections, data.parentPath);
        const item = section?.items?.[data.itemIdx];

        if (item?.kind === 'slide') {
          const slide = item.data;
          const isInSelection = selectedOutlineSlides.some(
            (selectedSlide) => selectedSlide.instanceId === slide.instanceId,
          );

          if (isInSelection && selectedOutlineSlides.length > 1) {
            return selectedOutlineSlides.map((selectedSlide) => ({
              displayName: selectedSlide.Title,
              thumbnailUrl: selectedSlide.ThumbnailLocation,
            }));
          }

          return [
            {
              displayName: slide.Title,
              thumbnailUrl: slide.ThumbnailLocation,
            },
          ];
        }
      }

      return [];
    },
    [sections, selectedSourceItems, selectedOutlineSlides],
  );

  const handleAddSection = useCallback(async () => {
    const allNames = new Set<string>();
    const collectNames = (list: PresentationOutlineSection<TaxonomyTreeSlide>[]) => {
      list.forEach((s) => {
        if (s.title) allNames.add(s.title);
        (s.items ?? []).forEach((item) => item.kind === 'section' && collectNames([item.data]));
      });
    };
    collectNames(sections);
    let counter = sections.length + 1;
    while (allNames.has(`Category ${counter}`)) counter++;
    const name = `Category ${counter}`;
    try {
      const node = await createTaxonomyNode({ name, order: sections.length, slideRepositoryId }).unwrap();
      setSections((prev) => [
        ...prev,
        { kind: 'section' as const, taxonomyId: node.ID, title: name, displayName: name, enabled: true, items: [] },
      ]);
    } catch (err) {
      console.error('Failed to create category', err);
    }
  }, [createTaxonomyNode, sections.length]);

  const handleRenameSection = useCallback(
    (path: number[], newName: string) => {
      setSections((prev) => {
        const section = getAtPath(prev, path);
        if (section?.locked) return prev;
        if (section?.taxonomyId) {
          updateTaxonomyNode({ nodeId: section.taxonomyId, data: { name: newName }, slideRepositoryId });
        }
        return updateAtPath(prev, path, (s) => ({ ...s, title: newName, displayName: newName }));
      });
    },
    [updateTaxonomyNode],
  );

  const handleRemoveSection = useCallback(
    (path: number[]) => {
      setSections((prev) => {
        const section = getAtPath(prev, path);
        if (section?.locked) return prev;
        if (section?.taxonomyId) {
          deleteTaxonomyNode({ nodeId: section.taxonomyId, slideRepositoryId });
        }
        return removeAtPath(prev, path).result;
      });
    },
    [deleteTaxonomyNode],
  );

  const handleToggleSection = useCallback(
    (path: number[]) => {
      setSections((prev) => {
        const section = getAtPath(prev, path);
        if (section?.locked) return prev;
        const nextVisible = section?.enabled === false;
        if (section?.taxonomyId) {
          updateTaxonomyNode({ nodeId: section.taxonomyId, data: { is_visible: nextVisible }, slideRepositoryId });
        }
        return updateAtPath(prev, path, (s) => ({ ...s, enabled: nextVisible }));
      });
    },
    [updateTaxonomyNode],
  );

  const handleOutlineSlideSelect = useCallback(
    (data: OutlineSlideSelectData) => {
      const section = getAtPath(sections, data.sectionPath);

      const slidesInSection = (section?.items ?? [])
        .filter((item) => item.kind === 'slide')
        .map((item) => item.data as unknown as OutlineSlide);
      selectOutlineSlide(data.slide, slidesInSection, data.event);
    },
    [sections, selectOutlineSlide],
  );

  const handleRemoveSlide = useCallback(
    (sectionPath: number[], itemIdx: number) => {
      setSections((prev) =>
        updateAtPath(prev, sectionPath, (section) => ({
          ...section,
          items: (section.items ?? []).filter((_, idx) => idx !== itemIdx),
        })),
      );
      clearOutlineSelection();
    },
    [clearOutlineSelection],
  );

  const handleUpdateSlide = useCallback((sectionPath: number[], itemIdx: number, purpose: string, query: string) => {
    setSections((prev) =>
      updateAtPath(prev, sectionPath, (section) => ({
        ...section,
        items: (section.items ?? []).map((item, idx) =>
          idx === itemIdx && item.kind === 'slide'
            ? { kind: 'slide' as const, data: { ...item.data, purpose, query } }
            : item,
        ),
      })),
    );
  }, []);

  const handleDndDragEnd = useCallback(
    (event: DragEndEvent) => {
      setIsExternalDragActive(false);

      const items = currentDraggedItemsRef.current;
      currentDraggedItemsRef.current = [];
      const { over } = event;

      if (!over || !items.length) {
        return;
      }

      const dropData = over.data.current as any;

      const removeFromSource = (prev: PresentationOutlineSection<TaxonomyTreeSlide>[]) => {
        if (!showSlidesMode || !activeSectionPath) return prev;
        const movedKeys = new Set(
          items.map((item: any) => {
            const sd = item?.itemData?.slideData || item?.itemData;
            return Number(sd?.SlideID ?? sd?.ID);
          }),
        );
        return updateAtPath(prev, activeSectionPath, (section) => ({
          ...section,
          items: (section.items ?? []).filter((it) => it.kind !== 'slide' || !movedKeys.has(it.data.SlideID)),
        }));
      };

      const clearSelection = () => {
        setSelectedSourceItems([]);
        clearSourceSelectionRef.current?.();
      };

      if (dropData?.type === 'gap') {
        setSections((prev) => {
          let next = removeFromSource(prev);
          items.forEach((item, i: number) => {
            next = updateAtPath(next, dropData.parentPath, (section) => {
              const newItems = [...(section.items ?? [])];
              newItems.splice(dropData.itemIdx + i, 0, { kind: 'slide' as const, data: itemToTaxonomySlide(item) });
              return { ...section, items: newItems };
            });
          });
          return next;
        });
        clearSelection();
      } else if (dropData?.type === 'sec-nest') {
        const isSameSection = activeSectionPath && dropData.path.join(',') === activeSectionPath.join(',');
        if (isSameSection) return;

        setSections((prev) => {
          let next = removeFromSource(prev);
          const targetSection = getAtPath(next, dropData.path);
          const insertIdx = targetSection?.items?.length ?? 0;
          items.forEach((item: any, i: number) => {
            next = updateAtPath(next, dropData.path, (section) => {
              const newItems = [...(section.items ?? [])];
              newItems.splice(insertIdx + i, 0, { kind: 'slide' as const, data: itemToTaxonomySlide(item) });
              return { ...section, items: newItems };
            });
          });
          return next;
        });
        clearSelection();
      }
    },
    [showSlidesMode, activeSectionPath],
  );

  const handleSectionsChange = useCallback((newSections: PresentationOutlineSection[]) => {
    setSections(newSections as unknown as PresentationOutlineSection<TaxonomyTreeSlide>[]);
  }, []);

  const handleAddToHierarchy = useCallback(
    async (itemsToAdd: any[]) => {
      if (!itemsToAdd.length) return;

      const newSlideItems = itemsToAdd.map((item) => ({
        kind: 'slide' as const,
        data: itemToTaxonomySlide(item),
      }));

      setSections((prev) => {
        if (prev.length) {
          return prev.map((section, idx) =>
            idx === prev.length - 1 ? { ...section, items: [...(section.items ?? []), ...newSlideItems] } : section,
          );
        }
        return prev;
      });

      if (!sections.length) {
        try {
          const node = await createTaxonomyNode({ name: 'Category 1', order: 0, slideRepositoryId }).unwrap();
          setSections([
            {
              kind: 'section',
              taxonomyId: node.ID,
              title: 'Category 1',
              displayName: 'Category 1',
              enabled: true,
              items: newSlideItems,
            },
          ]);
        } catch (err) {
          console.error('Failed to create category', err);
        }
      }
    },
    [createTaxonomyNode, sections.length],
  );

  const slidesPanelActionHandlers = useMemo(
    () => ({
      [FolderManagerItemActionNames.ADD_TO_CATEGORY]: async (action: any) => {
        handleAddToHierarchy(action.payload.selectedItems || []);
      },
      navigate: (action: any) => {
        const id = action.payload?.id as string;
        if (!id) return;
        setActiveSectionPath(id.split('-').map(Number));
      },
    }),
    [handleAddToHierarchy],
  );

  const handleSourceSelect = useCallback((data: { selectedRows: any[]; clearSelection: () => void }) => {
    setSelectedSourceItems(data.selectedRows);
    clearSourceSelectionRef.current = data.clearSelection;
  }, []);

  return (
    <CrossContainerDnDProvider
      onDragStartData={handleDragStartData}
      onDragStart={() => setIsExternalDragActive(true)}
      onDragEnd={handleDndDragEnd}
      onDragCancel={() => setIsExternalDragActive(false)}
    >
      <NgGridLayout leftColumnWidth="40%">
        <StyledColumnGrid as="aside">
          <StickyHeader top={STICKY_TOP} noWrapper>
            <NgCard>
              <NgCardContent>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <NgCardTitle title="Hierarchy" />
                  <NgBuildIcon show={isLoadingTaxonomy || isSaving} />
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                    <StyledTextButton onClick={() => outlineActionsRef.current?.expandAll()}>
                      Expand all
                    </StyledTextButton>
                    <StyledTextButton onClick={() => outlineActionsRef.current?.collapseAll()}>
                      Collapse all
                    </StyledTextButton>
                    Show slides
                    <Switch
                      size="small"
                      checked={showSlidesMode}
                      onChange={(e) => startTransition(() => setShowSlidesMode(e.target.checked))}
                    />
                  </div>
                </div>
                <StyledScrollOutline>
                  <PresentationOutline
                    sections={sections as unknown as PresentationOutlineSection[]}
                    selectedSlides={selectedOutlineSlides}
                    actionsRef={outlineActionsRef}
                    withDndContext={false}
                    forceExternalDrag={isExternalDragActive}
                    hideDragOverlay
                    hideReadingTime
                    hideSlides={!showSlidesMode}
                    sectionLabel="Category"
                    activeSectionPath={activeSectionPath}
                    onRename={handleRenameSection}
                    onRemove={handleRemoveSection}
                    onToggle={handleToggleSection}
                    onAddSection={handleAddSection}
                    onUpdateSlide={handleUpdateSlide}
                    onRemoveSlide={handleRemoveSlide}
                    onSlideSelect={handleOutlineSlideSelect}
                    onSectionSelect={handleSectionSelect}
                    onReorder={handleSectionsChange}
                    maxDepth={config.maxCategoriesDepth}
                    showSectionToggle={true}
                  />
                </StyledScrollOutline>
              </NgCardContent>
            </NgCard>
          </StickyHeader>
        </StyledColumnGrid>

        <StyledMainSection>
          <FolderManagerWithActions
            rootFolderId={0}
            isLoading={false}
            noDataMessage={!activeSectionPath ? 'Select a category to view its slides.' : undefined}
            hierarchyData={activeSectionSlides as any}
            breadcrumbs={activeSectionBreadcrumbs}
            selectedFolderData={null}
            selectedFolderId={null}
            viewStateKey={`${viewStateKey}-slides`}
            customActionHandlers={slidesPanelActionHandlers as any}
            onClearNewButtonAction={() => {}}
            onSelect={handleSourceSelect}
            acceptedDragTypes={TARGET_ACCEPTED_TYPES}
            stickyHeaderTop={STICKY_TOP}
            disableInternalDnD={true}
            disableUpload={true}
            minHeight="600px"
          />
        </StyledMainSection>
      </NgGridLayout>
    </CrossContainerDnDProvider>
  );
};
